<template>
  <v-card>
    <v-card-title class="pb-0 d-flex justify-space-between">
      <span>紹介売上</span>
      <v-btn
        color="primary"
        small
        @click="addReferralDialog = true"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiAccountPlus }}
        </v-icon>
        紹介先を追加
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- 紹介ツリー（直接紹介先一覧） -->
      <div
        v-if="referralTree.length > 0"
        class="mb-4"
      >
        <div class="text-subtitle-2 mb-2">
          紹介先顧客
        </div>
        <div class="d-flex flex-wrap">
          <v-chip
            v-for="item in referralTree"
            :key="item.customerId"
            class="mr-2 mb-2"
            close
            @click:close="confirmDeleteReferral(item)"
          >
            {{ item.customerName }}
            <span class="ml-1 text-caption">
              (¥{{ formatNumber(item.confirmedSales) }})
            </span>
          </v-chip>
        </div>
      </div>

      <div
        v-else-if="!isLoadingReferralTree"
        class="text-caption grey--text mb-4"
      >
        紹介先がありません
      </div>

      <!-- サマリー -->
      <div
        v-if="referralSalesSummary"
        class="d-flex mb-4"
      >
        <v-card
          outlined
          class="pa-3 mr-2 flex-grow-1 text-center"
        >
          <div class="text-caption grey--text">
            紹介売上合計
          </div>
          <div class="text-h6">
            ¥{{ formatNumber(referralSalesSummary.totalSales) }}
          </div>
        </v-card>
        <v-card
          outlined
          class="pa-3 mr-2 flex-grow-1 text-center"
        >
          <div class="d-flex align-center justify-center">
            <v-text-field
              v-model.number="ratePercent"
              type="number"
              dense
              hide-details
              suffix="%分"
              style="max-width: 80px;"
              class="text-caption"
              min="0"
              max="100"
            />
          </div>
          <div class="text-h6">
            ¥{{ formatNumber(calculatedTotalWithRate) }}
          </div>
        </v-card>
        <v-card
          outlined
          class="pa-3 flex-grow-1 text-center"
        >
          <div class="text-caption grey--text">
            紹介先顧客数
          </div>
          <div class="text-h6">
            {{ referralSalesSummary.descendantCount || 0 }}名
          </div>
        </v-card>
      </div>

      <!-- 説明 -->
      <div class="text-caption text--secondary mb-2">
        <div>全ての売上: 紹介先顧客の売上（小計ベース。値引きがある卓は値引き分を卓の大計の比率で控除）を集計します</div>
        <div>回収済みのみ: 通常支払い分（来店日基準）と期間内に回収した売掛金（回収日基準）を集計します。売上のない期間でも回収分は「売掛回収」行として計上されます</div>
        <div class="mt-1">
          <div>計算方式（金額は円未満切り捨て。値引きのない卓は売上(小計)そのままで、金額が売上(小計)を超えることはありません）:</div>
          <div>・全ての売上 = 売上(小計) × (大計 − 値引き) ÷ 大計</div>
          <div>・回収済みのみ = 売上(小計) × (大計 − 値引き) ÷ 大計 × (端数処理後の大計 − 売掛 + 期間内回収額) ÷ 端数処理後の大計</div>
          <div>・売掛回収行（売上が期間外の伝票のみ） = 売上(小計) × (大計 − 値引き) ÷ 大計 × 回収額 ÷ 端数処理後の大計</div>
          <div>※大計 = 税サ込みの合計、端数処理後の大計 = 会計時の請求額（値引き・手数料反映後）</div>
        </div>
      </div>

      <!-- 売上対象・期間フィルター -->
      <div class="d-flex align-center flex-wrap mb-3">
        <v-select
          v-model="referralSalesScope"
          :items="salesScopeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="min-width: 128px; max-width: 136px;"
          class="mr-3"
          @change="fetchData"
        />
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="referralSalesStartDate"
              label="開始日"
              dense
              hide-details
              readonly
              clearable
              class="mr-2"
              style="max-width: 160px;"
              v-bind="attrs"
              v-on="on"
              @click:clear="referralSalesStartDate = null; fetchData()"
            />
          </template>
          <v-date-picker
            v-model="referralSalesStartDate"
            no-title
            locale="ja"
            :day-format="formatDay"
            @input="startDateMenu = false; fetchData()"
          />
        </v-menu>
        <span class="mx-1">〜</span>
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="referralSalesEndDate"
              label="終了日"
              dense
              hide-details
              readonly
              clearable
              style="max-width: 160px;"
              v-bind="attrs"
              v-on="on"
              @click:clear="referralSalesEndDate = null; fetchData()"
            />
          </template>
          <v-date-picker
            v-model="referralSalesEndDate"
            no-title
            locale="ja"
            :day-format="formatDay"
            @input="endDateMenu = false; fetchData()"
          />
        </v-menu>
      </div>

      <!-- 紹介売上テーブル -->
      <v-skeleton-loader
        v-if="isLoadingReferralSales"
        type="table"
      />

      <v-simple-table
        v-else-if="referralSales.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th>日付</th>
              <th>伝票</th>
              <th>紹介先顧客</th>
              <th>指名者</th>
              <th class="text-right">
                売上（小計）
              </th>
              <th class="text-right">
                {{ ratePercent }}%
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in referralSales"
              :key="index"
            >
              <td>{{ item.date }}</td>
              <td>{{ item.receiptId ? `No.${item.receiptId}` : '-' }}</td>
              <td>
                {{ item.referredCustomerName }}
                <v-chip
                  v-if="item.rowType === 'collection'"
                  x-small
                  class="ml-1"
                >
                  売掛回収
                </v-chip>
                <v-chip
                  v-else-if="item.adjustment && item.adjustment.collectedAmount > 0"
                  x-small
                  class="ml-1"
                >
                  売掛回収含む
                </v-chip>
              </td>
              <td>{{ formatRecipientNames(item.recipientNames) }}</td>
              <td class="text-right">
                <v-tooltip
                  v-if="item.adjustment"
                  bottom
                  max-width="400"
                >
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >
                      ¥{{ formatNumber(item.salesAmount) }}
                      <v-icon x-small>
                        {{ icons.mdiInformationOutline }}
                      </v-icon>
                    </span>
                  </template>
                  <span>{{ formatAdjustmentFormula(item) }}</span>
                </v-tooltip>
                <template v-else>
                  ¥{{ formatNumber(item.salesAmount) }}
                </template>
              </td>
              <td class="text-right">
                ¥{{ formatNumber(calcWithRate(item.salesAmount)) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="grey lighten-3">
            <tr>
              <td
                colspan="4"
                class="font-weight-bold"
              >
                合計
              </td>
              <td class="text-right font-weight-bold">
                ¥{{ formatNumber(referralSalesSummary ? referralSalesSummary.totalSales : 0) }}
              </td>
              <td class="text-right font-weight-bold">
                ¥{{ formatNumber(calculatedTotalWithRate) }}
              </td>
            </tr>
          </tfoot>
        </template>
      </v-simple-table>

      <div
        v-else
        class="text-center text-caption grey--text py-4"
      >
        紹介売上データがありません
      </div>

      <!-- ページネーション -->
      <div
        v-if="referralSalesPagination.totalPages > 1"
        class="text-center mt-3"
      >
        <v-pagination
          :value="referralSalesPagination.page"
          :length="referralSalesPagination.totalPages"
          :total-visible="5"
          @input="handlePageChange"
        />
      </div>
    </v-card-text>

    <!-- 紹介先追加ダイアログ -->
    <v-dialog
      v-model="addReferralDialog"
      max-width="480"
      @click:outside="resetSearchState"
    >
      <v-card>
        <v-card-title>紹介先を追加</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="searchQuery"
            label="顧客名・会員番号で検索"
            dense
            hide-details
            class="mb-3"
            @input="handleSearchDebounced"
          />
          <v-list
            v-if="searchResults.length > 0"
            dense
            style="max-height: 300px; overflow-y: auto;"
          >
            <v-list-item
              v-for="c in searchResults"
              :key="c.id"
              @click="handleAddReferral(c)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ c.attributes.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="c.attributes.memberNumber">
                  会員番号: {{ c.attributes.memberNumber }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div
            v-else-if="searchQuery && !isSearching"
            class="text-center text-caption grey--text py-4"
          >
            該当する顧客が見つかりません
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="addReferralDialog = false; resetSearchState()"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 紹介関係解除の確認ダイアログ -->
    <v-dialog
      v-model="deleteConfirmDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>紹介関係の解除</v-card-title>
        <v-card-text>
          {{ deletingReferral ? deletingReferral.customerName : '' }} との紹介関係を解除しますか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteConfirmDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            @click="handleDeleteReferral"
          >
            解除する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiAccountPlus, mdiInformationOutline } from '@mdi/js'
import { debounce } from 'lodash'
import useCustomerReferrals from '../composables/useCustomerReferrals'
import { SALES_SCOPE_OPTIONS } from '../composables/useCustomerRanking'
import CustomerApi from '@/api/waiter/Customer'

export default {
  name: 'CustomerReferralSales',
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const {
      referralSales,
      referralSalesSummary,
      referralSalesPagination,
      referralTree,
      isLoadingReferralSales,
      isLoadingReferralTree,
      referralSalesStartDate,
      referralSalesEndDate,
      referralSalesScope,
      fetchReferralSales,
      fetchReferralTree,
      createReferral,
      deleteReferral,
    } = useCustomerReferrals(vm)

    const salesScopeOptions = SALES_SCOPE_OPTIONS

    const ratePercent = ref(10)
    const calculatedTotalWithRate = computed(() => {
      const total = referralSalesSummary.value ? referralSalesSummary.value.totalSales : 0

      return Math.round((total * ratePercent.value) / 100)
    })

    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const addReferralDialog = ref(false)
    const deleteConfirmDialog = ref(false)
    const deletingReferral = ref(null)
    const searchQuery = ref('')
    const searchResults = ref([])
    const isSearching = ref(false)

    const formatNumber = value => (value || 0).toLocaleString()

    const formatRecipientNames = names => {
      if (!names || names.length === 0) return '-'

      return names.join(', ')
    }

    const calcWithRate = amount => Math.round(((amount || 0) * ratePercent.value) / 100)

    // 調整の計算根拠（顧客説明用の式）。値引き・売掛は卓全体の比率で控除され、
    // 端数処理（店舗の大計の丸め設定）が入る場合は「→ 端数処理後」を付けて表示する
    const formatAdjustmentFormula = item => {
      const adj = item.adjustment
      if (!adj) return ''

      const result = adj.unroundedAmount === item.salesAmount
        ? `= ¥${formatNumber(item.salesAmount)}`
        : `= ¥${formatNumber(adj.unroundedAmount)} → 端数処理後 ¥${formatNumber(item.salesAmount)}`

      const parts = [`売上(小計) ¥${formatNumber(adj.rawAmount)}`]
      if (adj.discountFee) {
        parts.push(`× (卓の大計 ¥${formatNumber(adj.tableTotalWithTax)} − 値引き ¥${formatNumber(adj.discountFee)}) ÷ 卓の大計 ¥${formatNumber(adj.tableTotalWithTax)}`)
      }
      if (item.rowType === 'collection') {
        parts.push(`× 回収額 ¥${formatNumber(adj.collectedAmount)} ÷ 端数処理後の大計 ¥${formatNumber(adj.billedTotal)}`)
      } else if (adj.receivableTotal || adj.collectedAmount) {
        const terms = [`端数処理後の大計 ¥${formatNumber(adj.billedTotal)}`]
        if (adj.receivableTotal) terms.push(`− 売掛 ¥${formatNumber(adj.receivableTotal)}`)
        if (adj.collectedAmount) terms.push(`+ 回収額 ¥${formatNumber(adj.collectedAmount)}`)
        parts.push(`× (${terms.join(' ')}) ÷ 端数処理後の大計 ¥${formatNumber(adj.billedTotal)}`)
      }

      return `${parts.join(' ')} ${result}`
    }

    const formatDay = date => new Date(date).getDate().toString()

    const fetchData = () => {
      fetchReferralSales(props.customerId)
    }

    const handlePageChange = page => {
      fetchReferralSales(props.customerId, { page })
    }

    const resetSearchState = () => {
      searchQuery.value = ''
      searchResults.value = []
    }

    const handleSearch = async () => {
      if (!searchQuery.value) {
        searchResults.value = []

        return
      }
      isSearching.value = true
      try {
        const res = await CustomerApi.getCustomers({ q: searchQuery.value, perPage: 20 })
        const allResults = res.data.data || []
        searchResults.value = allResults.filter(
          c => Number(c.id) !== props.customerId,
        )
      } catch {
        searchResults.value = []
      } finally {
        isSearching.value = false
      }
    }

    const handleSearchDebounced = debounce(handleSearch, 300)

    const handleAddReferral = async customer => {
      const success = await createReferral({
        referrerId: props.customerId,
        referredId: Number(customer.id),
      })
      if (success) {
        addReferralDialog.value = false
        resetSearchState()
      }
    }

    const confirmDeleteReferral = item => {
      deletingReferral.value = item
      deleteConfirmDialog.value = true
    }

    const handleDeleteReferral = async () => {
      if (!deletingReferral.value) return
      await deleteReferral({
        referralId: deletingReferral.value.referralId,
        referrerId: props.customerId,
      })
      deleteConfirmDialog.value = false
      deletingReferral.value = null
    }

    onMounted(() => {
      fetchReferralTree(props.customerId)
      fetchReferralSales(props.customerId)
    })

    return {
      referralSales,
      referralSalesSummary,
      referralSalesPagination,
      referralTree,
      isLoadingReferralSales,
      isLoadingReferralTree,
      referralSalesStartDate,
      referralSalesEndDate,
      referralSalesScope,
      salesScopeOptions,
      ratePercent,
      calculatedTotalWithRate,
      startDateMenu,
      endDateMenu,
      addReferralDialog,
      deleteConfirmDialog,
      deletingReferral,
      searchQuery,
      searchResults,
      isSearching,
      formatNumber,
      formatRecipientNames,
      formatAdjustmentFormula,
      calcWithRate,
      formatDay,
      fetchData,
      handlePageChange,
      resetSearchState,
      handleSearchDebounced,
      handleAddReferral,
      confirmDeleteReferral,
      handleDeleteReferral,
      icons: {
        mdiAccountPlus,
        mdiInformationOutline,
      },
    }
  },
}
</script>
